import type { PlaylistSortKey } from '@/lib/types/Playlist';
import type { MediaType } from '@/lib/types/Media';

// --- as const arrays and derived types ---

export const LIST_TYPES = ['custom', 'tag'] as const;
export type ListType = typeof LIST_TYPES[number];

export const LIST_SORT_TYPES = ['published', 'title'] as const;
export type ListSortType = typeof LIST_SORT_TYPES[number];

export const TAG_LOGICAL_OPERATORS = ['and', 'or', 'not'] as const;
export type TagLogicalOperator = typeof TAG_LOGICAL_OPERATORS[number];

// --- ID mappings for embed URLs (content service routing) ---

/** Numeric IDs used in embed URL paths. Matches backend ListType.listTypeToId(). */
export const PLAYLIST_TYPE_IDS: Record<ListType, number> = {
  custom: 1,
  tag: 2,
} as const;

/** Numeric IDs for media types in embed URLs. Matches backend MediaTypeUtil.getId(). */
export const MEDIA_TYPE_IDS: Record<string, number> = {
  video: 1,
  audio: 2,
  image: 3,
  data: 4,
  video_stream: 5,
  audio_stream: 6,
} as const;

// --- Constraints ---

export const PLAYLIST_CONSTRAINTS = {
  TITLE_MIN: 1,
  TITLE_MAX: 150,
  DESC_SHORT_MAX: 500,
  REF_ID_MAX: 100,
  MAX_TAG_RULES: 3,
  MAX_LIMIT: 500,
} as const;

export const LIST_TYPE_LABELS = {
  custom: 'Manual',
  tag: 'Smart',
} as const;

// UI nouns for a playlist's items, keyed by the playlist's mediaType. Only the
// types creatable in v3 get real nouns; legacy types (audio etc.) use the fallback.
export interface PlaylistMediaNoun {
  singular: string;
  plural: string;
  pluralTitle: string;
}

export const PLAYLIST_MEDIA_NOUNS: Partial<Record<MediaType, PlaylistMediaNoun>> = {
  video: { singular: 'video', plural: 'videos', pluralTitle: 'Videos' },
  video_stream: { singular: 'live stream', plural: 'live streams', pluralTitle: 'Live Streams' },
} as const;

export const PLAYLIST_MEDIA_NOUN_FALLBACK: PlaylistMediaNoun = {
  singular: 'item',
  plural: 'items',
  pluralTitle: 'Items',
} as const;

// Insert position for items added to a manual playlist (picker modal choice)
export const PLAYLIST_ADD_POSITIONS = ['end', 'beginning'] as const;
export type PlaylistAddPosition = typeof PLAYLIST_ADD_POSITIONS[number];

export const PLAYLIST_ADD_POSITION_LABELS: Record<PlaylistAddPosition, string> = {
  end: 'End',
  beginning: 'Beginning',
} as const;

// "Add to Playlist" modal (Videos/Streams grids): the playlist list is rendered
// pre-filtered and hard-capped (bounded-options rule) — search narrows the rest.
export const ADD_TO_PLAYLIST_MAX_OPTIONS = 100;

export const TAG_LOP_LABELS = {
  and: 'has all',
  or: 'has any',
  not: 'does not have',
} as const;

export const LIST_SORT_UI_OPTIONS = [
  { value: 'published_desc', label: 'Published Date (Newest First)', sortType: 'published', sortDirection: 'desc' },
  { value: 'published_asc', label: 'Published Date (Oldest First)', sortType: 'published', sortDirection: 'asc' },
  { value: 'title_asc', label: 'Title (A-Z)', sortType: 'title', sortDirection: 'asc' },
] as const;

export const PLAYLIST_QUERY_KEYS = {
  all: (cId: number) => ['playlists', cId] as const,
  customDetail: (cId: number, listId: number) => ['playlists', cId, 'custom', listId] as const,
  tagDetail: (cId: number, listId: number) => ['playlists', cId, 'tag', listId] as const,
} as const;

export const PLAYLIST_GRID_COLUMNS = [
  { key: 'checkbox', label: '', sortable: false },
  { key: 'title', label: 'Title', sortable: true, sortKey: 'title' as PlaylistSortKey },
  { key: 'type', label: 'Type', sortable: true, sortKey: 'listType' as PlaylistSortKey },
  { key: 'status', label: 'Status', sortable: true, sortKey: 'state' as PlaylistSortKey },
  { key: 'created', label: 'Created', sortable: true, sortKey: 'created' as PlaylistSortKey },
  { key: 'updated', label: 'Updated', sortable: true, sortKey: 'updated' as PlaylistSortKey },
  { key: 'actions', label: '', sortable: false },
] as const;

/** Default sort when no persisted preference exists: alphabetical by title */
export const DEFAULT_PLAYLIST_SORT_KEY: PlaylistSortKey = 'title';
export const DEFAULT_PLAYLIST_SORT_DIRECTION = 'asc' as const;
export const PLAYLIST_SORT_KEYS = ['title', 'listType', 'state', 'created', 'updated'] as const;

export const PLAYLIST_ROW_HEIGHT = 56;
export const PLAYLIST_VIRTUALIZER_OVERSCAN = 10;
export const PLAYLIST_GRID_MAX_VIRTUAL_ROWS = 72;
export const PLAYLIST_GRID_MAX_VIEWPORT_HEIGHT_PX = 2200;
